<template>
  <DownloadsModal
    v-if="showDownloadsModal"
    :plants="plants"
    @closeModal="showDownloadsModal = false"
  />
  <IngredientsCartModal
    v-if="showIngredientsCartModal"
    :type="ingredientCartType"
    :plants="plants"
    @closeModal="showIngredientsCartModal = false"
  />
  <div class="schedule-a-view" data-cy="schedule-a-view">
    <div class="schedule-tabs">
      <UITabContainer
        v-if="tabs.length"
        class="schedule-tab-container"
        :tabs="tabs"
      >
        <template v-slot:tabHeaderContent>
          <div class="schedule-actions">
            <LoadingButton
              type="button"
              theme-color="primary"
              :loading="!viewLoaded"
              @click="showDownloadsModal = true"
              data-cy="download-schedule-a"
              >Download Schedule A</LoadingButton
            >
            <div class="badge-container-wrapper" v-if="canOpenOrModifyCarts">
              <BadgeContainer
                v-for="action in cartActions"
                :key="action.subApplication"
                content="content"
                rounded="full"
                :theme-color="action.theme"
                size="large"
                :cutout-border="true"
              >
                <LoadingButton
                  type="button"
                  :theme-color="action.theme"
                  :loading="!viewLoaded || !action.loaded"
                  :disabled="action.count === 0"
                  @click="action.onClick"
                  :data-cy="`complete-order-${action.subApplication}`"
                  >{{ action.name
                  }}<font-awesome-icon icon="fa-solid fa-cart-shopping" />
                </LoadingButton>
                <template v-slot:content>{{ action.count }}</template>
              </BadgeContainer>
            </div>
          </div>
        </template>
        <template v-slot:scheduleA>
          <ScheduleATable
            v-if="viewLoaded"
            :plants="plants"
            :canModifyCarts="canOpenOrModifyCarts"
          />
          <TableSkeleton v-else />
        </template>
        <template v-slot:kosherDatabase>
          <UkdIngredientsTable
            v-if="viewLoaded"
            :plants="plants"
            :canModifyCarts="canOpenOrModifyCarts"
          />
          <TableSkeleton v-else />
        </template>
      </UITabContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref } from "vue";
import type { IdAndName } from "@/Domain/IdAndName";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import { BadgeContainer } from "@progress/kendo-vue-indicators";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { useScheduleAStore } from "@/stores/useScheduleAStore";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import TableSkeleton from "@/shared/ui/skeletons/TableSkeleton.vue";
import UITabContainer from "@/shared/ui/tabs/tab-container.vue";
import ScheduleATable from "@/views/scheduleA/ScheduleATable.vue";
import DownloadsModal from "@/views/scheduleA/DownloadsModal.vue";
import IngredientsCartModal from "@/views/scheduleA/IngredientsCartModal.vue";
import UkdIngredientsTable from "@/views/scheduleA/UkdIngredientsTable.vue";
import { IngredientsAppGroup } from "@/shared/access/RoleGroups";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";

const main = useMainStore();
const scheduleStore = useScheduleAStore();

const plants = ref<IdAndName[]>([]);
const viewLoaded = ref(false);
const showDownloadsModal = ref(false);
const showIngredientsCartModal = ref(false);
const ingredientCartType = ref<SubApplicationType>(SubApplicationType.Add);

// Ukd Search Authorization - Hide UKD tab for unauthorized users
const tabs = [{ id: "scheduleA", label: "Schedule A" }];

const certifiedPlants = computed(() => {
  return plants.value.filter((p) => p.Status == EnumCompanyStatus.Certified);
});

if (
  main.companySummary?.denials.every(
    (d) => !d.UKD || d.UKD.toUpperCase() !== "ALL"
  )
) {
  tabs.push({ id: "kosherDatabase", label: "Universal Kosher Database" });
}

const canOpenOrModifyCarts = computed(() => {
  return (
    (main.companySummary &&
      main.companySummary.denials.every(
        (d) => !d.IA || d.IA.toUpperCase() !== "ALL"
      ) &&
      IngredientsAppGroup.roles.some((r) => main.isInRole(r))) ??
    false
  );
});

const cartActions = ref([
  {
    name: "Complete order: Add Ingredients",
    count: scheduleStore.getCartCount(SubApplicationType.Add),
    subApplication: SubApplicationType.Add,
    loaded: false,
    theme: "success",
    onClick: () => {
      ingredientCartType.value = SubApplicationType.Add;
      showIngredientsCartModal.value = true;
    },
  },
  {
    name: "Complete order: Delete Ingredients",
    count: scheduleStore.getCartCount(SubApplicationType.Terminate),
    subApplication: SubApplicationType.Terminate,
    loaded: false,
    theme: "error",
    onClick: () => {
      ingredientCartType.value = SubApplicationType.Terminate;
      showIngredientsCartModal.value = true;
    },
  },
]);

const getPlantsAsync = async function () {
  return api.get("GetAuthorizedCertifiedOrPendingOwns", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    certifiedOnly: false,
  });
};

const updateIngredientsInCartCountAsync = async function () {
  const addAction = cartActions.value.find(
    (action) => action.subApplication === SubApplicationType.Add
  )!;
  const terminateAction = cartActions.value.find(
    (action) => action.subApplication === SubApplicationType.Terminate
  )!;

  const cartPromise = api.get("VueGetIngCartCount", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
  });

  await handleResponsePromiseAsync(
    cartPromise,
    (data) => {
      scheduleStore.setCartCount(addAction.subApplication, data.adds);
      addAction.loaded = true;

      scheduleStore.setCartCount(terminateAction.subApplication, data.deletes);
      terminateAction.loaded = true;
    },
    "ScheduleAView",
    "updateIngredientsInCartCountAsync"
  );
};

const updatePlantListAsync = async function () {
  return handleResponsePromiseAsync(
    getPlantsAsync(),
    (data) => {
      plants.value = data.filter((p) => p.Status !== EnumCompanyStatus.Special);
    },
    "ScheduleBView",
    "updatePlantListAsync"
  );
};

const onCartCountUpdate = function (type: SubApplicationType, count: number) {
  const action = cartActions.value.find(
    (action) => action.subApplication === type
  );
  if (action) {
    action.count = count;
  }
};

scheduleStore.handleCartCountUpdate = onCartCountUpdate;

onBeforeMount(async () => {
  updateIngredientsInCartCountAsync();
  await updatePlantListAsync();
  viewLoaded.value = true;
});
</script>

<style lang="scss">
.schedule-a-view {
  .schedule-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    align-items: center;
    margin-left: auto;

    .badge-container-wrapper {
      display: flex;
      gap: 1rem;
    }

    .fa-cart-shopping {
      margin-left: 0.5rem;
      width: 12px;
    }
  }
}
</style>
